import React, { useEffect, useState } from "react";
import perf from "./perf";
import { devtoolsParams, clearDevtools } from "./devtools/params";
import { renderer } from "./render";

const SAMPLE_INTERVAL = 500;

// Counting live elements walks the whole map subtree, so it is sampled far less
// often than the rest of the HUD.
const ELEMENT_COUNT_INTERVAL = 4000;

const styles = {
  container: {
    position: "absolute",
    top: 0,
    left: 0,
    zIndex: 1000,
    padding: "6px 10px",
    background: "rgba(0, 0, 0, 0.75)",
    color: "#eee",
    font: "11px/1.45 Consolas, monospace",
    whiteSpace: "pre",
    pointerEvents: "none",
    borderBottomRightRadius: "6px",
  },
  heading: { color: "#7dd", fontWeight: "bold" },
  warn: { color: "#fb0" },
  bad: { color: "#f77" },
  fake: {
    background: "#b00",
    color: "#fff",
    fontWeight: "bold",
    padding: "2px 6px",
    marginBottom: "4px",
    pointerEvents: "auto",
    cursor: "pointer",
  },
};

// The devtools flags persist for the tab so they survive the Azure AD redirect,
// which makes it possible to be looking at fabricated data without realising.
function syntheticSource() {
  const params = devtoolsParams();
  if (params.has("synth"))
    return "SYNTHETIC DATA (?synth) — not the real estate";
  if (params.has("tape"))
    return "REPLAYED TAPE (" + params.get("tape") + ") — not live";
  return null;
}

function fmt(ms) {
  return ms.toFixed(1).padStart(6);
}

function valueStyle(ms) {
  if (ms >= 33) return styles.bad;
  if (ms >= 16.7) return styles.warn;
  return null;
}

const PerfHud = () => {
  const [snapshot, setSnapshot] = useState(null);
  const [elementCount, setElementCount] = useState(null);
  const [rate, setRate] = useState(0);

  useEffect(() => {
    if (!perf.enabled) return;

    let lastMessages = null;
    let lastSampledAt = null;

    const sample = () => {
      const snap = perf.snapshot();

      const now = performance.now();
      const messages = snap.counters.messages;
      if (lastMessages != null && now > lastSampledAt) {
        setRate(((messages - lastMessages) * 1000) / (now - lastSampledAt));
      }
      lastMessages = messages;
      lastSampledAt = now;

      setSnapshot(snap);
    };

    sample();
    const timer = window.setInterval(sample, SAMPLE_INTERVAL);

    const countElements = () => {
      const active = renderer();
      if (active) setElementCount(active.elementCount());
    };
    countElements();
    const elementTimer = window.setInterval(
      countElements,
      ELEMENT_COUNT_INTERVAL
    );

    return () => {
      window.clearInterval(timer);
      window.clearInterval(elementTimer);
    };
  }, []);

  if (!perf.enabled || !snapshot) return null;

  const { frame, callback, sections, counters } = snapshot;

  const sectionKeys = Object.keys(sections).sort(
    (a, b) => sections[b].p95 - sections[a].p95
  );

  const fakeSource = syntheticSource();

  return (
    <div style={styles.container}>
      {fakeSource && (
        <div
          style={styles.fake}
          onClick={() => {
            clearDevtools();
            window.location.href = window.location.pathname;
          }}
          title="Click to clear and return to the live server"
        >
          {fakeSource} — click to return to live
        </div>
      )}
      <div style={styles.heading}>
        {(frame.p50 > 0 ? 1000 / frame.p50 : 0).toFixed(0)} fps
        {"   frame ms   p50 "}
        <span style={valueStyle(frame.p50)}>{fmt(frame.p50)}</span>
        {"  p95 "}
        <span style={valueStyle(frame.p95)}>{fmt(frame.p95)}</span>
        {"  p99 "}
        <span style={valueStyle(frame.p99)}>{fmt(frame.p99)}</span>
        {"  max "}
        <span style={valueStyle(frame.max)}>{fmt(frame.max)}</span>
      </div>
      <div>
        {"        in callback  p50 "}
        <span style={valueStyle(callback.p50)}>{fmt(callback.p50)}</span>
        {"  p95 "}
        <span style={valueStyle(callback.p95)}>{fmt(callback.p95)}</span>
        {"  p99 "}
        <span style={valueStyle(callback.p99)}>{fmt(callback.p99)}</span>
        {"  max "}
        <span style={valueStyle(callback.max)}>{fmt(callback.max)}</span>
      </div>

      {sectionKeys.map((key) => (
        <div key={key}>
          {key.padEnd(16)}
          {"mean "}
          {fmt(sections[key].mean)}
          {"  p95 "}
          <span style={valueStyle(sections[key].p95)}>
            {fmt(sections[key].p95)}
          </span>
          {"  max "}
          {fmt(sections[key].max)}
        </div>
      ))}

      <div style={styles.heading}>
        {"objects "}
        {counters.objects}
        {"  orphans "}
        {counters.orphans}
        {"  elements "}
        {elementCount == null ? "?" : elementCount}
      </div>
      <div style={styles.heading}>
        {"animations "}
        {counters.animations}
        {"  tracked "}
        {counters.tracked}
        {"  layout "}
        {counters.layoutSolves}
        {" solves / "}
        {counters.layoutRequests}
        {" reqs / "}
        {snapshot.frames}
        {" frames"}
      </div>
      {counters.batches != null && (
        <div style={styles.heading}>
          {"batches "}
          {counters.batches}
          {"  rebuilt "}
          {counters.rebuilt}
          {"  updated "}
          {counters.updated}
          {"  verts "}
          {counters.vertices}
        </div>
      )}
      <div style={styles.heading}>
        {"msgs "}
        {counters.messages}
        {" ("}
        {rate.toFixed(0)}
        {"/s)  queue "}
        {counters.queue}
      </div>
    </div>
  );
};

export default PerfHud;
